import React, { useState, useEffect, useRef } from "react";
import { useStateValue } from "./StateProvider";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import ReactToPrint from "react-to-print";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { loadAssets, loadReadings } from './Utils/dataloader.js';
import configData from './Config';

import { getReadingsData, getUsageReport } from './Utils/readings.js';
import { registerArabicFont } from "./Utils/registerarabicfont.js";

import Title from "./Title";
import Select from './Components/select.js';

import "./Reports.css";

function Reports({ state, setState }) {
  const [{ project }, dispatch] = useStateValue();

  const [from, setFrom] = useState(project.startdate ? new Date(project.startdate * 1000) : new Date());
  const [fromts, setFromTs] = useState(0);
  const [to, setTo] = useState(new Date());
  const [tots, setToTs] = useState(0);

  const [option, setOption] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    loadAssets(project, state, dispatch);
    loadReadings(project, state, dispatch);
  }, []);

  useEffect(() => {
    const startOfDay = new Date(from);
    startOfDay.setHours(0, 0, 0, 0);
    const fromTs = Math.floor(startOfDay.getTime() / 1000);
    setFromTs(fromTs);
  }, [from]);

  useEffect(() => {
    const endOfDay = new Date(to);
    endOfDay.setHours(23, 59, 59, 999);
    const toTs = Math.floor(endOfDay.getTime() / 1000);
    setToTs(toTs);
  }, [to]);

  const handleReadingsReport = () => {
    setOption(<ReadingsReport />);
  };

  const handleOverallReport = () => {
    setOption(<OverallReport />);
  };


  function ReadingsReport() {
    const [assetid, setAssetid] = useState('');

    const [loadingpdf, setLoadingpdf] = useState(false);
    const [resultpdf, setResultpdf] = useState('');

    const printRef = useRef();

    const context = {
      readings: project.readings,
      assetid: assetid,
      fromts: fromts,
      tots: tots,
    };

    let readingsdata = getReadingsData(context);
    const assetName = project.assets.find(asset => asset.id == assetid)?.name || '';

    const saveAsPDF = async () => {
      setLoadingpdf(true);
      try {
        const projectName = project.name;

        const doc = new jsPDF();
        registerArabicFont(doc);

        const pageWidth = doc.internal.pageSize.getWidth();
        let currentY = 10;

        currentY += 10;
        doc.setFontSize(24);
        const title = 'Readings Report';
        const titleWidth = doc.getTextWidth(title);
        doc.text(title, (pageWidth - titleWidth) / 2, currentY);

        currentY += 10;
        doc.setFontSize(10);
        doc.text('Project Name: ' + projectName, 10, currentY);

        currentY += 5;
        doc.text('Asset Name: ' + assetName, 10, currentY);

        currentY += 5;
        const formattedFrom = moment.unix(fromts).format("D/MMM/YYYY");
        const formattedTo = moment.unix(tots).format("D/MMM/YYYY");
        doc.text('From: ' + formattedFrom, 10, currentY);
        currentY += 5;
        doc.text('To: ' + formattedTo, 10, currentY);

        currentY += 10;
        const tableColumn = ['Date', 'Reading', 'Difference'];

        const tableData = readingsdata.map((data) => {
          let date = moment.unix(data.ts).format("D/MMM/YYYY");
          return [
            { content: date, styles: { fontStyle: 'normal' } },
            { content: data.reading, styles: { fontStyle: 'normal' } },
            { content: data.difference, styles: { fontStyle: 'normal' } },
          ];
        });

        autoTable(doc, {
          head: [tableColumn],
          body: tableData,
          startY: currentY,
          theme: 'grid',
          headStyles: {
            fillColor: [200, 200, 200],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 10,
            textColor: [0, 0, 0],
            font: 'notoarabic',
          },
          columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'left' },
            2: { halign: 'left' },
            3: { halign: 'left' },
          },
          margin: { top: 10, left: 10, right: 10 },
        });

        const fileName = 'Readings Report ' + assetName + '.pdf';
        doc.save(fileName.replace(/\s+/g, '_').toLowerCase());
        setResultpdf("Saved Successfully");
      } catch (error) {
        console.error('Error generating PDF:', error);
        setResultpdf("Error Saving PDF");
      } finally {
        setLoadingpdf(false);
      }
    };

    return (
      <div className="report">
        <div className="reports_row">
          <div className="reports_rowsection">
            Asset
          </div>
          <div className="reports_rowsection">
            <Select data={project.assets} itemid={assetid} setItemid={setAssetid} />
          </div>
        </div>
        <div className="reports_buttonscontainer">
          <ReactToPrint
            trigger={() => (<button className="reports_button">Print</button>)}
            content={() => printRef.current}
          />
          <button className="reports_button" onClick={saveAsPDF}>
            Save as PDF {loadingpdf && '(Loading...)'} {resultpdf && "(" + resultpdf + ")"}
          </button>
        </div>
        <div className="reports_printable" ref={printRef}>
          <div className="reports_headerrow">
            <div className="reports_headercolleft">
              {project.projectdetailsimage && (
                <img
                  className="reports_projectdetailsimage"
                  src={project.projectdetailsimage}
                  alt="Project Details Image"
                />
              )}
            </div>
            <div className="reports_headercolmiddle">
              <div className="reports_title">Readings Report</div>
              <div className="reports_projectname">{project.name}</div>
              <div className="reports_dateframe">
                {moment.unix(fromts).format("D/MMM/YYYY") + ' to ' + moment.unix(tots).format("D/MMM/YYYY")}
              </div>
            </div>
            <div className="reports_headercolright"></div>
          </div>
          <table className='reports_table'>
            <thead>
              <tr>
                <th>Date</th>
                <th>Reading</th>
                <th>Difference</th>
              </tr>
            </thead>
            <tbody>
              {
                readingsdata.map((reading, index) => {
                  let date = moment.unix(reading.ts).format("D/MMM/YYYY");
                  return (
                    <tr key={'reading' + index}>
                      <td>{date}</td>
                      <td>{reading.reading}</td>
                      <td>{reading.difference}</td>
                    </tr>
                  )
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function OverallReport() {

  }

  return (
    <div className="reports">
      <Title text="reports" />

      <div className="reports_row">
        <div className="reports_rowsection">From</div>
        <div className="reports_rowsection">
          <DatePicker
            className="reports_datepicker"
            selected={from}
            onChange={(date) => setFrom(date)}
          />
        </div>
        <div className="reports_rowsection">To</div>
        <div className="reports_rowsection">
          <DatePicker
            className="reports_datepicker"
            selected={to}
            onChange={(date) => setTo(date)}
          />
        </div>
      </div>
      <div className="reports_buttonscontainer">
        <button className="reports_button" onClick={handleReadingsReport}>Readings Report</button>
        <button className="reports_button" onClick={handleOverallReport}>Overall Report</button>
      </div>

      {option}

    </div>
  );
}

export default Reports;
